import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { IStudentInfo } from '../../../shared/models/student';
import { EYesNo } from '../../../shared/models/types.enum';
import { DateUtils } from '../../../shared/helpers/dateUtils';
import { StudentService } from '../../../shared/services/student.service';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { PopupMessageComponent } from '../popup-message/popup-message.component';

@Component({
  selector: 'student-info-review',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent,
    PopupMessageComponent
  ],
  templateUrl: './student-info-review.component.html',
  styleUrl: './student-info-review.component.css'
})
export class StudentInfoReviewComponent implements OnInit {

  yesNo = EYesNo;
  icons = Icons;
  @ViewChild('datepickerBirthdate') datepickerBirthdate!: ElementRef;

  @Input() studentInfo: IStudentInfo | null = null;
  @Output() onClose = new EventEmitter<void>();
  @Output() onContinue = new EventEmitter<void>();

  studentInfoReviewForm: FormGroup = new FormGroup({});
  student_birthday_to_display: string | null = null;
  buildingId: string | null = null;
  regionId: string | null = null;
  popupMessage: string | null = null;
  messageModal: string | null = null;

  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private cdRef: ChangeDetectorRef,
    private studentService: StudentService,
    private userDataService: UserDataService
  ) {}

  ngOnInit(): void {
    this.student_birthday_to_display = DateUtils.formatDateForDisplay(this.studentInfo!.birthday);
    this.buildingId = this.userDataService.getBuildingId();
    this.regionId = this.userDataService.getRegionId();

    this.initializeForm();    // initialize the form
    this.onChanges();    // set up form change handlers if user changes dob or has_iep
  }

  ngOnChanges(changes: SimpleChanges): void {
    // grab the studentInfo object and initialize the form
    if (changes['studentInfo'] && this.studentInfo) {
      this.initializeForm();
    }
  }

  private onChanges(): void {    
    this.studentInfoReviewForm.get('has_iep')!.valueChanges.subscribe(val => {
      const iepContactControl = this.studentInfoReviewForm.get('iep_contact');
      if (val === this.yesNo.Yes) {
        iepContactControl?.enable();
        iepContactControl?.addValidators(Validators.required);
      } else {
        iepContactControl?.clearValidators();
        iepContactControl?.setValue('');
      }
      iepContactControl?.updateValueAndValidity();
      this.cdRef.detectChanges();
    });
  }

  private initializeForm(): void {
    this.studentInfoReviewForm = new FormGroup({
      grade: new FormControl(this.studentInfo?.grade, Validators.required),
      physical_description: new FormControl(this.studentInfo?.physical_description, Validators.required),
      has_iep: new FormControl(String(this.studentInfo?.has_iep), Validators.required),
      iep_contact: new FormControl({value: this.studentInfo?.iep_contact, disabled: this.studentInfo?.has_iep === 0}),
      has_ihp: new FormControl(String(this.studentInfo?.has_ihp), Validators.required),
      has_504: new FormControl(String(this.studentInfo?.has_504), Validators.required)
    });
  }

  handleCloseStudentInfo(): void {
    this.onClose.emit();
  }

  updateStudentInfo(): void {
    if (this.studentInfoReviewForm.valid && this.buildingId && this.regionId) {
      this.loading = true;
      const formValue = this.studentInfoReviewForm.value;
        formValue.grade = Number(formValue.grade);
        formValue.has_iep = Number(formValue.has_iep);
        formValue.has_ihp = Number(formValue.has_ihp);
        formValue.has_504 = Number(formValue.has_504);

        formValue.student_identifier = this.studentInfo?.student_identifier;
        formValue.building_id = this.buildingId;
        this.studentService.updateStudent(this.regionId, formValue).subscribe({
          next: (response) => {
            this.loading = false;
            this.onContinue.emit();
          },
          error: (error) => {
            this.loading = false;
            console.error('Error updating student:', error);
            this.messageModal = 'unsuccess';
            this.popupMessage = error.message;
          }
        });
    } else {
      return
    }
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
